import { useState, useCallback } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PLACE_TYPE_ICONS } from './map-constants';

interface MapFiltersProps {
    // Place filters
    availablePlaceTypes: string[];
    selectedPlaceTypes: Set<string>;
    onPlaceTypesChange: (types: Set<string>) => void;
    placeTypeCounts: Map<string, number>;

    // Event filters
    availableEventCategories: string[];
    selectedEventCategories: Set<string>;
    onEventCategoriesChange: (categories: Set<string>) => void;
    eventCategoryCounts: Map<string, number>;

    className?: string;
}

export function MapFilters({
    availablePlaceTypes,
    selectedPlaceTypes,
    onPlaceTypesChange,
    placeTypeCounts,
    availableEventCategories,
    selectedEventCategories,
    onEventCategoriesChange,
    eventCategoryCounts,
    className = ''
}: MapFiltersProps) {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['places', 'events'])
    );

    const toggleSection = useCallback((section: string) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    }, []);

    const handlePlaceTypeToggle = useCallback((type: string) => {
        const next = new Set(selectedPlaceTypes);
        if (next.has(type)) {
            next.delete(type);
        } else {
            next.add(type);
        }
        onPlaceTypesChange(next);
    }, [selectedPlaceTypes, onPlaceTypesChange]);

    const handleEventCategoryToggle = useCallback((category: string) => {
        const next = new Set(selectedEventCategories);
        if (next.has(category)) {
            next.delete(category);
        } else {
            next.add(category);
        }
        onEventCategoriesChange(next);
    }, [selectedEventCategories, onEventCategoriesChange]);

    const handleSelectAllPlaces = useCallback(() => {
        onPlaceTypesChange(new Set(availablePlaceTypes));
    }, [availablePlaceTypes, onPlaceTypesChange]);

    const handleClearAllPlaces = useCallback(() => {
        onPlaceTypesChange(new Set());
    }, [onPlaceTypesChange]);

    const handleSelectAllEvents = useCallback(() => {
        onEventCategoriesChange(new Set(availableEventCategories));
    }, [availableEventCategories, onEventCategoriesChange]);

    const handleClearAllEvents = useCallback(() => {
        onEventCategoriesChange(new Set());
    }, [onEventCategoriesChange]);

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Place Type Filters */}
            <div className="space-y-2">
                <button
                    onClick={() => toggleSection('places')}
                    className="flex items-center gap-2 w-full text-sm font-semibold hover:text-primary transition-colors"
                >
                    {expandedSections.has('places') ? (
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                        <ChevronRight className="h-4 w-4" />
                    )}
                    <span>📍 Place Types</span>
                    <Badge variant="secondary" className="ml-auto">
                        {selectedPlaceTypes.size}/{availablePlaceTypes.length}
                    </Badge>
                </button>

                {expandedSections.has('places') && (
                    <div className="space-y-2 pl-6">
                        {/* Quick actions */}
                        <div className="flex gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSelectAllPlaces}
                                className="h-7 text-xs"
                            >
                                Select All
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClearAllPlaces}
                                className="h-7 text-xs"
                            >
                                Clear All
                            </Button>
                        </div>

                        {/* Place type checkboxes */}
                        <div className="space-y-2">
                            {availablePlaceTypes.map((type) => {
                                const count = placeTypeCounts.get(type) || 0;
                                const isDisabled = count === 0;

                                return (
                                    <div
                                        key={type}
                                        className={`flex items-center gap-2 ${
                                            isDisabled ? 'opacity-50' : ''
                                        }`}
                                    >
                                        <Checkbox
                                            id={`place-${type}`}
                                            checked={selectedPlaceTypes.has(type)}
                                            onCheckedChange={() => handlePlaceTypeToggle(type)}
                                            disabled={isDisabled}
                                        />
                                        <label
                                            htmlFor={`place-${type}`}
                                            className="flex items-center gap-2 text-sm cursor-pointer flex-1"
                                        >
                                            <span>{PLACE_TYPE_ICONS[type] || '📍'}</span>
                                            <span className="capitalize">{type.replace('-', ' ')}</span>
                                            <Badge variant="outline" className="ml-auto text-xs">
                                                {count}
                                            </Badge>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Event Category Filters */}
            <div className="space-y-2">
                <button
                    onClick={() => toggleSection('events')}
                    className="flex items-center gap-2 w-full text-sm font-semibold hover:text-primary transition-colors"
                >
                    {expandedSections.has('events') ? (
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                        <ChevronRight className="h-4 w-4" />
                    )}
                    <span>🕐 Event Categories</span>
                    <Badge variant="secondary" className="ml-auto">
                        {selectedEventCategories.size}/{availableEventCategories.length}
                    </Badge>
                </button>

                {expandedSections.has('events') && (
                    <div className="space-y-2 pl-6">
                        {/* Quick actions */}
                        <div className="flex gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSelectAllEvents}
                                className="h-7 text-xs"
                            >
                                Select All
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClearAllEvents}
                                className="h-7 text-xs"
                            >
                                Clear All
                            </Button>
                        </div>

                        {/* Event category checkboxes */}
                        <div className="space-y-2">
                            {availableEventCategories.map((category) => {
                                const count = eventCategoryCounts.get(category) || 0;
                                const isDisabled = count === 0;

                                return (
                                    <div
                                        key={category}
                                        className={`flex items-center gap-2 ${
                                            isDisabled ? 'opacity-50' : ''
                                        }`}
                                    >
                                        <Checkbox
                                            id={`event-${category}`}
                                            checked={selectedEventCategories.has(category)}
                                            onCheckedChange={() => handleEventCategoryToggle(category)}
                                            disabled={isDisabled}
                                        />
                                        <label
                                            htmlFor={`event-${category}`}
                                            className="flex items-center gap-2 text-sm cursor-pointer flex-1"
                                        >
                                            <span className="capitalize">{category.replace('-', ' ')}</span>
                                            <Badge variant="outline" className="ml-auto text-xs">
                                                {count}
                                            </Badge>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

